import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";

import { NextUp } from "@/components/Callouts";

export default function EarTraining10() {
  const { lesson, course, accent } = findLesson("ear-training", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET10-Melody-Flowchart-Animated.mp4"
        caption="Happy Birthday end-to-end — home · curve · intervals · notation"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="10" eyebrow="Ear Training · Chapter Ten · Course finale" title="The complete melody flowchart — and the road from here">
        <Prose>
          <p>
            The matching flowchart for melody. Same idea, different content. Six steps and
            you've transcribed any tune.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/01a-melody-flowchart-overall.png"
          caption="Jason's complete melody flowchart. Listen & Sing Along → Key, Scale & Signature → Essential Theory (the piano worm, scale circle, triads, cadences) → Melodic Curves → Intervals (sa/ga/ma, M3/tritone/m7) → Staff Notation with Rhythm. Six steps, every melody."
        />
      </Chapter>

      <Chapter eyebrow="The six steps" title="What each one is for">
        <Steps>
          <Step><strong>Listen & sing along.</strong> Internalise the tune. If you can hum it without the recording, you have everything you need to transcribe.</Step>
          <Step><strong>Key, scale & signature.</strong> Use chapters 1-2: tonic, major-or-minor, modal flavour.</Step>
          <Step><strong>Essential theory.</strong> Once you know the key, recall the diatonic chords (chapter 7), the cadences (chapter 2), and the scale shape (the piano worm from MT-6).</Step>
          <Step><strong>Melodic curves.</strong> Sketch the silhouette using chapter 5's technique. Higher / lower / same.</Step>
          <Step><strong>Intervals.</strong> Walk the curve and identify each interval using chapter 4&apos;s drill. Now you have pitches.</Step>
          <Step><strong>Staff notation with rhythm.</strong> Combine the pitches with the rhythm (chapters 3, 8, 9) and write it out.</Step>
        </Steps>
      </Chapter>

      <Chapter eyebrow="Functional harmony, recap" title="The chord families that drive a song">
        <NoteImage
          src="/notes/ear-training/finals/02a-functional-harmony-Tonic-PreDominant-Dominant.png"
          caption="One last look at functional harmony. Tonic (I, vi, iii) — home. Pre-dominant (ii, IV) — the journey. Dominant (V, vii°, V7) — the pull back. Mediant (iii) — the gentle bridge. Once you can hear chords by their function rather than their name, you can transcribe any song's harmony at the speed of listening."
        />

        <NoteImage
          src="/notes/ear-training/finals/02b-cadences-G-major-examples.png"
          caption="The four cadences as your final ear-training reference. Authentic V→I, Plagal IV→I, Deceptive V→vi, Half ?→V. Endings tell you what kind of song it is."
        />
      </Chapter>

      <Chapter eyebrow="Where you go from here" title="Practice — and don't stop">
        <Prose>
          <p>
            Ten chapters can teach you the framework. The actual <em>training</em> is in the
            repetitions. Here's a 90-day routine that turns this course into a real ear:
          </p>
          <ul>
            <li><strong>Daily (10 minutes).</strong> Run the IntervalEarTrainer (chapter 4) on melodic mode until you score 18/20. Then switch to harmonic.</li>
            <li><strong>Weekly (one song).</strong> Pick a song you don&apos;t know. Apply both flowcharts. Write out the chord chart and the lead sheet from scratch.</li>
            <li><strong>Monthly (one full transcription).</strong> Pick something harder — a jazz standard, a film cue, a classical piece. Do the full melody-and-harmony transcription, two staves with rhythm. Compare against the published score after.</li>
          </ul>
        </Prose>

        <Prose>
          <p className="text-center text-emerald-300">
            <strong>That&apos;s the course. Now go and transcribe something.</strong>
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/courses/ear-training"
        label="Back to the course index"
        why="You&apos;ve completed Ear Training. Pick another course or replay any chapter."
      />
    </LessonShell>
  );
}
